import React, { useMemo, useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import WallpaperIcon from '@mui/icons-material/Wallpaper'

import BackgroundLibraryGrid from './BackgroundLibraryGrid'
import { useBackgroundLibrary } from '../hooks/useBackgroundLibrary'
import {
  ACCEPTED_UPLOAD_TYPES,
  MAX_BACKGROUND_UPLOAD_BYTES,
  MAX_WORKSPACE_BACKGROUNDS,
  type LibraryBackground,
} from '../services/backgroundLibrary'
import { backgroundEffectKey } from '../services/liveKitVideoBackground'
import { isVirtualBackgroundEnabled } from '../config/mediaConfig'
import { useSelectedBackgroundEffect } from '../hooks/useSelectedBackgroundEffect'

export interface CompanyBackgroundSettingsProps {
  disabled?: boolean
}

/**
 * Company virtual backgrounds, for admins and managers (SC-158).
 *
 * Unlike every other tab in the Edit Company dialog, this one **saves as you
 * go**. Uploading or removing an image takes effect immediately rather than
 * waiting for the dialog's Update Company button — a file upload has no
 * meaningful draft state, and pretending otherwise would mean holding megabytes
 * in memory until someone remembered to press it. The note in the header says
 * so, because in a dialog that otherwise saves on Update Company it is a fair
 * thing to wonder.
 *
 * Scoped to the caller's **own** company: the API derives the company from the
 * session, so this is only rendered for a manager editing their own company,
 * never for a platform admin editing someone else's.
 *
 * Members can *use* these backgrounds; only admins and managers can change the
 * set. The server enforces that on every write — this surface just reflects it.
 */
export const CompanyBackgroundSettings: React.FC<CompanyBackgroundSettingsProps> = ({ disabled = false }) => {
  // The deployment flag only, not the device probe: uploading and deleting need
  // no segmentation, so a manager on a machine without WebGL2 can still manage
  // the set their colleagues use.
  const available = useMemo(() => isVirtualBackgroundEnabled(), [])
  const library = useBackgroundLibrary(available)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // The admin's own camera background: not deletable while they use it. Other
  // members who use one that is deleted are told on their own camera (F2).
  const [ownEffect] = useSelectedBackgroundEffect()

  const workspaceBackgrounds = library.backgrounds.filter(
    (background: LibraryBackground) => background.kind === 'workspace'
  )
  const atCap = workspaceBackgrounds.length >= MAX_WORKSPACE_BACKGROUNDS

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset first, so re-picking the same file still fires a change event.
    event.target.value = ''
    if (file) await library.upload(file, 'workspace')
  }

  if (!available) {
    return (
      <Alert severity="info" data-testid="background-settings-unavailable">
        Virtual backgrounds are not enabled for this deployment.
      </Alert>
    )
  }

  return (
    <Box data-testid="company-background-settings">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WallpaperIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6">Company backgrounds</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Images everyone in your company can pick as a video background. Changes here
        are saved straight away - you don’t need to click Update Company.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {library.loadFailed && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="background-settings-load-error"
          action={
            <Button color="inherit" size="small" onClick={library.reload}>
              Retry
            </Button>
          }
        >
          {library.loadError ??
            'Couldn’t load your company’s backgrounds. Check your connection and retry.'}
        </Alert>
      )}

      {/* Only from a real answer: a failed request says nothing about the role. */}
      {!library.loading && !library.loadFailed && !library.canManageWorkspace && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="background-settings-readonly">
          Only company admins and managers can change these.
        </Alert>
      )}

      {library.error && (
        <Alert severity="warning" onClose={library.clearError} sx={{ mb: 2 }}>
          {library.error}
        </Alert>
      )}

      {library.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : library.loadFailed ? null : workspaceBackgrounds.length > 0 ? (
        <BackgroundLibraryGrid
          backgrounds={workspaceBackgrounds}
          // Nothing is "selected" here: this manages the set, it does not choose
          // an effect. A key that matches no background leaves every tile plain.
          activeKey=""
          inUseKey={backgroundEffectKey(ownEffect)}
          // Nothing to select: this manages the set, it does not choose an effect.
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSelect={() => {}}
          onDelete={
            library.canManageWorkspace
              ? (background) => void library.remove(background.id)
              : undefined
          }
          disabled={disabled || library.busy}
          columns={4}
        />
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          No company backgrounds yet.
        </Typography>
      )}

      {library.canManageWorkspace && !library.loadFailed && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={
              library.busy ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />
            }
            // Stopped here as well as on the server, so an admin finds out
            // before picking a file rather than after uploading one.
            disabled={disabled || library.busy || atCap}
            onClick={() => fileInputRef.current?.click()}
            data-testid="company-background-upload"
            sx={{ textTransform: 'none' }}
          >
            Add a background
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
            {atCap
              ? `Your company has all ${MAX_WORKSPACE_BACKGROUNDS} backgrounds. Remove one to add another.`
              : `${workspaceBackgrounds.length} of ${MAX_WORKSPACE_BACKGROUNDS} used · JPEG, PNG or WebP, up to ${Math.round(
                  MAX_BACKGROUND_UPLOAD_BYTES / (1024 * 1024)
                )} MB. Large images are scaled down before they are stored.`}
          </Typography>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_UPLOAD_TYPES.join(',')}
        hidden
        data-testid="company-background-file-input"
        onChange={(event) => void handleFile(event)}
      />
    </Box>
  )
}

export default CompanyBackgroundSettings
